import { LinearProgress, Stack } from "@mui/material";
import { take } from "lodash";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useAgents } from "../admin/agents/useAgents";
import { useAuthContext } from "../auth/AuthContext";
import type { UserRole } from "../auth/auth";
import { getAgentIdIfPresent } from "../auth/auth";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import { useCustomers } from "../customers/useCustomers";
import { useLimit } from "../misc/useLimit";
import { TaskCard } from "./TaskCard";
import type { TaskFilters } from "./TaskSearch";
import TaskSearch from "./TaskSearch";
import { filterTasks } from "./filterTasks";
import { useTask } from "./useEntityTask";
import { useTasks } from "./useTasks";

interface Props {
  readonly role: UserRole;
}

const TasksPage: FC<Props> = ({ role }) => {
  const { context, user } = useAuthContext();
  const { tasks, isLoading: areTasksLoading } = useTasks(false);
  const { completeTask, deleteTask, updateTask } = useTask();
  const { agents, isLoading: areAgentsLoading } = useAgents();
  const { customers, isLoading: areCustomersLoading } = useCustomers();
  const { limit, loadMore } = useLimit();

  const [filters, setFilters] = useState<TaskFilters>({
    query: "",
    assignedUserId: getAgentIdIfPresent(context) ?? null,
  });

  useEffect(() => {
    setFilters((f) => ({ ...f, assignedUserId: getAgentIdIfPresent(context) ?? null }));
  }, [context]);

  const isLoading = areTasksLoading || areAgentsLoading || areCustomersLoading;

  const filteredTasks = filterTasks(tasks, filters);

  const tasksToShow = take(filteredTasks, limit).map((t) => {
    const customerId = t.parent.customerId;
    const customer = customers.find((c) => c.id === customerId);
    const createdByAgent = agents.find((a) => a.loginEmail === t.createdBy);
    return {
      id: t.id,
      due: t.due,
      description: t.description,
      parent: t.parent,
      completed: t.completed,
      assignedUser: { id: t.assignedUser, name: agents.find((a) => a.id === t.assignedUser)?.name ?? "n.d." },
      assignedUserEmail: t.assignedUser,
      customer: customer ? { id: customer.id, name: customer.name } : undefined,
      createdByEmail: t.createdBy,
      createdBy: createdByAgent ? { name: createdByAgent.name } : undefined,
    };
  });

  return (
    <Stack spacing={1} sx={{ marginTop: 1, height: "100%" }}>
      <TaskSearch
        filters={filters}
        onChange={(change) => setFilters((f) => ({ ...f, ...change }))}
        onClear={() => setFilters({ query: "", assignedUserId: null })}
      />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredTasks.length ? limit : filteredTasks.length}
        fetchData={loadMore}
        hasMore={limit < filteredTasks.length}
      >
        {tasksToShow.map((task) => (
          <TaskCard
            key={task.id}
            value={task}
            role={role}
            canDelete={task.createdByEmail === user?.email}
            onClick={() => {}}
            onComplete={async (n) => {
              await completeTask(task.parent.type, task.parent.id, task.id, n);
            }}
            onDelete={() => deleteTask(task.parent.type, task.parent.id, task.id)}
            onUpdate={async (request) => {
              await updateTask(task.parent.type, task.parent.id, task.id, request);
            }}
          />
        ))}
      </InfiniteScroll>
    </Stack>
  );
};

export default TasksPage;
